/**
 * As subtarefas de uma tarefa, dentro do painel de detalhe.
 *
 * `parent_id` existia no DDL desde a fase 0, `createTaskSchema` o aceitava e
 * `rowToTask` o devolvia — e **nenhuma tela sabia criar ou mostrar uma
 * subtarefa**. Mais uma da classe de defeito da §8f, e a ultima da lista da
 * §10.
 *
 * ## A filha continua aparecendo nas listas
 *
 * A escolha obvia seria escondê-la das visoes (Hoje, Todas, matriz,
 * calendario), como fazem os apps que tratam subtarefa como item de checklist.
 * Aqui isso seria **esconder trabalho** — e foi justamente a queixa que gerou a
 * §8g, quando "Todas" nao era todas. Uma subtarefa com prazo para hoje e
 * trabalho de hoje; ela nao deixa de ser porque nasceu ligada a outra coisa.
 *
 * O preco: a mesma tarefa aparece na lista e dentro do pai. Preferivel ao
 * inverso — uma subtarefa com prazo que nao aparece em lugar nenhum.
 */
import { useState, type FormEvent } from 'react';
import type { Task } from '@cleartask/shared';
import {
  newTaskId,
  useCompleteTask,
  useCreateTask,
  useReopenTask,
  useTaskList,
} from './useTasks.js';

export function Subtarefas({ pai }: { pai: Task }) {
  const criar = useCreateTask();
  const concluir = useCompleteTask();
  const reabrir = useReopenTask();
  const [titulo, setTitulo] = useState('');

  // `includeDone`: a subtarefa concluida FICA na lista, riscada. Some-la daria
  // a impressao de que o pai encolheu sozinho, e o valor de uma decomposicao e
  // justamente ver o que ja saiu.
  const { data, isPending } = useTaskList({
    view: 'list',
    includeDone: true,
    parentId: pai.id,
  });

  const filhas = data?.tasks ?? [];
  const feitas = filhas.filter((t) => t.status === 'done').length;

  function adicionar(event: FormEvent) {
    event.preventDefault();
    const nome = titulo.trim();
    if (nome === '') return;

    criar.mutate({
      id: newTaskId(),
      title: nome,
      parentId: pai.id,
      // Herda a lista do pai, e nada mais. Prazo e prioridade sao decisoes
      // sobre ESTA etapa — carimbar as do pai inventaria uma data que ninguem
      // digitou, que e a mesma razao pela qual o "+" da matriz nao data.
      listId: pai.listId,
    });

    // Continua aberto e vazio: decompor uma tarefa vem em rajada.
    setTitulo('');
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm text-text-mut">
        Subtarefas{' '}
        {filhas.length > 0 ? (
          <span className="tabular-nums">
            {feitas}/{filhas.length}
          </span>
        ) : null}
      </h3>

      {isPending ? (
        <p className="text-sm text-text-mut">Carregando…</p>
      ) : (
        <ul className="flex flex-col">
          {filhas.map((filha) => {
            const concluida = filha.status === 'done';

            return (
              <li key={filha.id} className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={concluida}
                  // Reabrir e `PATCH { status: 'open' }`, nunca a rota de
                  // concluir: repetir `POST /complete` numa tarefa ja concluida
                  // duplicaria a proxima ocorrencia de uma serie (§7).
                  onChange={() => (concluida ? reabrir.mutate(filha) : concluir.mutate(filha))}
                  aria-label={`${concluida ? 'Reabrir' : 'Concluir'} ${filha.title}`}
                  className="size-5 shrink-0 accent-brand-blue"
                />
                <span className={concluida ? 'text-text-mut line-through' : ''}>
                  {filha.title}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={adicionar} className="flex gap-2">
        <input
          value={titulo}
          onChange={(event) => setTitulo(event.target.value)}
          placeholder="Nova subtarefa"
          aria-label="Nova subtarefa"
          className="min-h-touch flex-1 rounded border border-border bg-surface px-3 text-base"
        />
        {/*
          Botao visivel, e nao so Enter: um formulario com um campo submete por
          Enter de graca, mas no celular o teclado mostra "ir" e nem todo mundo
          associa as duas coisas — e a spec 10.5 pede alvo alcancavel por toque.
        */}
        <button
          type="submit"
          disabled={titulo.trim() === '' || criar.isPending}
          className="min-h-touch rounded bg-surface-2 px-3 font-medium disabled:opacity-50"
        >
          Adicionar
        </button>
      </form>
    </section>
  );
}

/**
 * O aviso, no detalhe de uma FILHA, de que ela faz parte de outra tarefa.
 *
 * Sem isto, abrir uma subtarefa por link (ou pelo card na lista, onde ela
 * aparece solta) nao daria pista nenhuma de que ela pertence a algo maior —
 * e concluir uma etapa sem saber de qual trabalho ela faz parte e decidir no
 * escuro.
 */
export function ParteDe({ paiId, onAbrirPai }: { paiId: string; onAbrirPai: () => void }) {
  const { data } = useTaskList({ view: 'list', includeDone: true });
  const pai = (data?.tasks ?? []).find((t) => t.id === paiId);

  return (
    <p className="text-sm text-text-mut">
      Parte de{' '}
      <button type="button" onClick={onAbrirPai} className="underline">
        {/*
          O titulo sai da consulta que a tela ja tem em maos. Quando ela nao o
          contem — a lista pode estar filtrada, ou o pai na lixeira —, o botao
          continua levando ao pai: um rotulo generico ainda navega, e uma
          segunda requisicao so para escrever o nome custaria mais do que
          entrega.
        */}
        {pai?.title ?? 'a tarefa principal'}
      </button>
    </p>
  );
}
